import moment from 'moment';

export const DEFAULT_SERVICE_INTERVAL_KM = 5000;
export const DEFAULT_SERVICE_INTERVAL_MONTHS = 6;

export const hasValue = (value) => {
  if (value === null || typeof value === 'undefined') return false;
  if (typeof value === 'number') return Number.isFinite(value);
  if (typeof value === 'string') return value.trim() !== '';
  return true;
};

export const pickField = (obj, ...keys) => {
  if (!obj || typeof obj !== 'object') return undefined;
  const found = keys.find((key) => obj[key] != null && obj[key] !== '');
  return found ? obj[found] : undefined;
};

export const formatDate = (value) => {
  if (!hasValue(value)) return '-';
  const parsed = moment(value);
  if (!parsed.isValid()) return String(value);
  return parsed.format('DD MMM YYYY');
};

export const formatDateTime = (value) => {
  if (!hasValue(value)) return '-';
  const parsed = moment(value);
  if (!parsed.isValid()) return String(value);
  return parsed.format('DD MMM YYYY, h:mm A');
};

export const isApiResponseSuccess = (result) => {
  const status = result?.data?.status !== undefined
    ? result.data.status
    : result?.status;
  return typeof status === 'number' && status >= 200 && status < 300;
};

/** Resolve HTTP or envelope status from an axios result/error object. */
export const getApiResponseStatus = (result) => {
  if (result?.data?.status !== undefined) {
    return result.data.status;
  }
  if (result?.response?.status !== undefined) {
    return result.response.status;
  }
  return result?.status;
};

export const isApiResponseNotFound = (result) => getApiResponseStatus(result) === 404;

/** Parse API response expecting a single object payload. */
export const parseApiObjectResponse = (result) => {
  if (!isApiResponseSuccess(result)) return null;
  const data = result?.data?.data ?? result?.data;
  if (data && typeof data === 'object' && !Array.isArray(data)) return data;
  return null;
};

/** Parse API response expecting a list payload. */
export const parseApiListResponse = (result) => {
  if (!isApiResponseSuccess(result)) return null;
  const data = result?.data?.data ?? result?.data;
  if (Array.isArray(data)) return data;
  if (data && typeof data === 'object' && Array.isArray(data.items)) return data.items;
  if (data && typeof data === 'object' && Array.isArray(data.history)) return data.history;
  return [];
};

export const getMomentTimestamp = (dateRaw) => {
  if (!hasValue(dateRaw)) return 0;
  const parsed = moment(dateRaw);
  return parsed.isValid() ? parsed.valueOf() : 0;
};

export const getReportSortTimestamp = (report) => {
  const raw = report?.rawRecord || {};
  const receiptInfo = raw.receiptInfo || {};
  const dateRaw = raw.updatedAt
    || raw.updated_at
    || raw.createdAt
    || raw.created_at
    || receiptInfo.serviceDate
    || receiptInfo.receiptDate;
  return getMomentTimestamp(dateRaw);
};

export const sortReportsByRecency = (reportLogs) => {
  if (!Array.isArray(reportLogs) || reportLogs.length === 0) return [];
  return [...reportLogs].sort(
    (a, b) => getReportSortTimestamp(b) - getReportSortTimestamp(a),
  );
};

const extractLastServiceFromReport = (report) => {
  const raw = report?.rawRecord || {};
  const receiptInfo = raw.receiptInfo || {};
  const assetIdentification = raw.assetIdentification || {};
  const serviceDate = receiptInfo.serviceDate || receiptInfo.receiptDate;
  const { odometerReading } = assetIdentification;
  const parsedDate = hasValue(serviceDate) ? moment(serviceDate) : null;
  const lastServiceDate = parsedDate && parsedDate.isValid() ? parsedDate.format('YYYY-MM-DD') : '';
  const odometerNum = hasValue(odometerReading) ? Number(odometerReading) : NaN;
  const lastServiceOdometer = Number.isFinite(odometerNum) ? String(Math.round(odometerNum)) : '';
  return { lastServiceDate, lastServiceOdometer };
};

/** Prefer the newest report with both fields; otherwise merge newest date and odometer separately. */
export const getLatestReportLastServiceFromSorted = (sortedReports) => {
  if (!Array.isArray(sortedReports) || sortedReports.length === 0) return null;

  let mergedDate = '';
  let mergedOdometer = '';

  for (let i = 0; i < sortedReports.length; i += 1) {
    const { lastServiceDate, lastServiceOdometer } = extractLastServiceFromReport(sortedReports[i]);

    if (hasValue(lastServiceDate) && hasValue(lastServiceOdometer)) {
      return { lastServiceDate, lastServiceOdometer };
    }

    if (!hasValue(mergedDate) && hasValue(lastServiceDate)) {
      mergedDate = lastServiceDate;
    }
    if (!hasValue(mergedOdometer) && hasValue(lastServiceOdometer)) {
      mergedOdometer = lastServiceOdometer;
    }

    if (hasValue(mergedDate) && hasValue(mergedOdometer)) {
      return { lastServiceDate: mergedDate, lastServiceOdometer: mergedOdometer };
    }
  }

  if (!hasValue(mergedDate) && !hasValue(mergedOdometer)) return null;
  return { lastServiceDate: mergedDate, lastServiceOdometer: mergedOdometer };
};

/** Next service date/mileage from the most recent maintenance report. */
export const getLatestReportSchedulingFromSorted = (sortedReports) => {
  if (!Array.isArray(sortedReports) || sortedReports.length === 0) return null;
  for (let i = 0; i < sortedReports.length; i += 1) {
    const scheduling = sortedReports[i]?.rawRecord?.schedulingAndWarranty || {};
    const { nextServiceDueDate, nextServiceDueMileage } = scheduling;
    if (hasValue(nextServiceDueDate) || hasValue(nextServiceDueMileage)) {
      return { nextServiceDueDate, nextServiceDueMileage };
    }
  }
  return null;
};

const computeKmRemaining = (nextOdometer, currentOdometer) => {
  if (!hasValue(nextOdometer) || !hasValue(currentOdometer)) return null;
  const next = Number(nextOdometer);
  const current = Number(currentOdometer);
  if (!Number.isFinite(next) || !Number.isFinite(current)) return null;
  return Math.round(next - current);
};

const formatLastServiceDate = (value) => {
  if (!hasValue(value)) return '';
  const parsed = moment(value);
  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : '';
};

const formatLastServiceOdometer = (value) => {
  if (!hasValue(value)) return '';
  const num = Number(value);
  return Number.isFinite(num) ? String(Math.round(num)) : '';
};

/** Prefer persisted schedule baseline; fall back to maintenance reports when unset. */
export const resolveLastServiceBaseline = (sortedReportLogs, serviceSchedule) => {
  const savedDate = pickField(serviceSchedule, 'lastServiceDate', 'last_service_date');
  const savedOdometer = pickField(serviceSchedule, 'lastServiceOdometer', 'last_service_odometer');

  if (hasValue(savedDate) || hasValue(savedOdometer)) {
    return {
      lastServiceDate: formatLastServiceDate(savedDate),
      lastServiceOdometer: formatLastServiceOdometer(savedOdometer),
    };
  }

  const fromReport = getLatestReportLastServiceFromSorted(sortedReportLogs);
  return {
    lastServiceDate: fromReport?.lastServiceDate || '',
    lastServiceOdometer: fromReport?.lastServiceOdometer || '',
  };
};

export const hasSavedServiceScheduleConfig = (data) => {
  if (!data || typeof data !== 'object') return false;
  return hasValue(pickField(data, 'serviceIntervalKm', 'service_interval_km'))
    || hasValue(pickField(data, 'serviceIntervalMonths', 'service_interval_months'));
};

export const mapServiceScheduleToForm = (data, sortedReportLogs = []) => {
  const lastServiceBaseline = resolveLastServiceBaseline(sortedReportLogs, data);
  const savedIntervalKm = pickField(data, 'serviceIntervalKm', 'service_interval_km');
  const savedIntervalMonths = pickField(data, 'serviceIntervalMonths', 'service_interval_months');

  return {
    lastServiceDate: lastServiceBaseline.lastServiceDate,
    lastServiceOdometer: lastServiceBaseline.lastServiceOdometer,
    serviceIntervalKm: hasValue(savedIntervalKm)
      ? String(savedIntervalKm)
      : String(DEFAULT_SERVICE_INTERVAL_KM),
    serviceIntervalMonths: hasValue(savedIntervalMonths)
      ? String(savedIntervalMonths)
      : String(DEFAULT_SERVICE_INTERVAL_MONTHS),
  };
};

export const buildServiceSchedulePayload = (form) => ({
  lastServiceDate: form.lastServiceDate || null,
  lastServiceOdometer: form.lastServiceOdometer !== '' ? Number(form.lastServiceOdometer) : null,
  serviceIntervalKm: form.serviceIntervalKm !== '' ? Number(form.serviceIntervalKm) : null,
  serviceIntervalMonths: form.serviceIntervalMonths !== '' ? Number(form.serviceIntervalMonths) : null,
});

/**
 * Resolve next-service display values.
 *
 * Ownership split (service-schedule API vs client):
 * - Backend: last-service config, intervals, nextServiceDate, nextServiceOdometer
 * - Client: currentMileageKm (live payload mileageKm via mileageSource) and kmRemaining
 *   (nextServiceOdometer − currentMileageKm). Do not read live mileage from service-schedule.
 */
export const resolveNextServiceScheduleDisplay = (
  sortedReportLogs = [],
  serviceSchedule,
  deviceCurrentMileageKm = null,
) => {
  const hasSavedSchedule = hasSavedServiceScheduleConfig(serviceSchedule);
  const apiNextOdometer = pickField(serviceSchedule, 'nextServiceOdometer', 'next_service_odometer');
  const apiNextDate = pickField(serviceSchedule, 'nextServiceDate', 'next_service_date');

  const latestReportScheduling = getLatestReportSchedulingFromSorted(sortedReportLogs);
  const scheduleFromReports = hasValue(latestReportScheduling?.nextServiceDueMileage)
    || hasValue(latestReportScheduling?.nextServiceDueDate);

  let nextServiceOdometer;
  let nextServiceDate;
  if (hasSavedSchedule && (hasValue(apiNextOdometer) || hasValue(apiNextDate))) {
    nextServiceOdometer = apiNextOdometer;
    nextServiceDate = apiNextDate;
  } else if (scheduleFromReports) {
    nextServiceOdometer = latestReportScheduling?.nextServiceDueMileage;
    nextServiceDate = latestReportScheduling?.nextServiceDueDate;
  } else {
    nextServiceOdometer = apiNextOdometer;
    nextServiceDate = apiNextDate;
  }

  const currentMileageKm = hasValue(deviceCurrentMileageKm) ? deviceCurrentMileageKm : null;
  const kmRemaining = computeKmRemaining(nextServiceOdometer, currentMileageKm);

  return {
    hasSavedSchedule,
    scheduleFromReports,
    nextServiceOdometer,
    nextServiceDate,
    currentMileageKm,
    kmRemaining,
  };
};

export const hasPersistedLastServiceFields = (serviceSchedule) => ({
  lastServiceDate: hasValue(pickField(serviceSchedule, 'lastServiceDate', 'last_service_date')),
  lastServiceOdometer: hasValue(pickField(serviceSchedule, 'lastServiceOdometer', 'last_service_odometer')),
});
